<template>
  <PopupHelper
    text="Device configuration has changed. Clicking this button will create a new method adapted to work with the new configuration. It is recommended to recheck the new method before using the device."
  >
    <Btn
      type="primary"
      height="s"
      padding="sm"
      iconLeft="build"
      :isLoading="isLoading"
      @click.stop="fixMethod"
    >
      Fix method
    </Btn>
  </PopupHelper>
</template>

<script>
  import MethodAPI from 'api/method';
  import { prepareMethodForRequest } from 'utils/methodHelpers.ts';
  import { apiDevices } from '@/api/graphql/cloud/devices';
  import Btn from '@/uikitBase/btns/Btn.vue';
  import { apiMethods } from '@/api/graphql/cloud/methods';
  import PopupHelper from '@/uikitProject/popups/info/PopupHelper.vue';

  const EVENT_FIX = 'fix';

  export default {
    name: 'FixMethodButton',

    components: {
      Btn,
      PopupHelper,
    },

    props: {
      method: {
        type: Object,
        required: true,
      },
      configuration: {
        type: Object,
        required: true,
      },
      deviceId: {
        type: String,
        required: true,
      },
    },

    data() {
      return {
        isLoading: false,
      };
    },

    methods: {
      async fixMethod() {
        if (this.isLoading) {
          return;
        }

        this.isLoading = true;

        try {
          const method = await apiMethods.getMethod(this.method.id);

          const methodForRequest = prepareMethodForRequest({
            ...method,
            id: undefined,
            name: `${method.name}`,
            favorite: false,
            hardware_configuration: this.configuration,
            device_id: this.deviceId,
          });

          const { promise, resolve, reject } = Promise.withResolvers();

          let createdMethod = null;

          MethodAPI.post(
            methodForRequest,
            (method) => {
              createdMethod = method;
              resolve(method);
            },
            () => {
              reject();
            },
          );

          await promise;

          if (createdMethod == null) {
            throw new Error('Failed to create method');
          }

          try {
            await apiDevices.updateDeviceMethod(this.deviceId, createdMethod.id);
            this.notify(`Method was fixed and selected`, 10);
            this.$emit(EVENT_FIX, createdMethod);
          } catch (selectError) {
            await this.deleteMethod(createdMethod.id);
            this.notifyError(`Can't select this method!`, { durationSeconds: 10 });
          }

          this.$store.commit('resetMethods');
        } catch (error) {
          this.notifyError('Error fixing the method', { durationSeconds: 10 });
        } finally {
          this.isLoading = false;
        }
      },

      async deleteMethod(methodId) {
        try {
          await apiMethods.removeMethod(methodId);
        } catch (error) {
          console.error('Error deleting method:', error);
        }
      },
    },
  };
</script>
